import AccountSelect from "../components/AccountSelect";
import React, { useState, useEffect, useMemo } from "react";
import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, ChevronLeft, Loader2, Search, Edit2, ArrowUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { CurrencyInput } from "../components/ui/currency-input";
import CurrencySelect from "../components/CurrencySelect";
import { useTheme } from "../App";
import { api, useAuth } from "../App";

const getLocalDateInputValue = (value = new Date()) => {
  const date = new Date(value);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};
const isFutureDateValue = (value) => {
  if (!value) return false;
  const selectedDate = new Date(`${value}T00:00:00`);
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return selectedDate >= tomorrow;
};
import { toast } from "@/lib/toast";

const CHART_COLORS = ["#396BE9","#62BF92","#F59E0B","#EF4444","#8B5CF6","#06B6D4","#EC4899","#84CC16","#6B7280"];

function getCatIconUrl(name) {
  const norm = (name || "").toLowerCase().trim();
  if (norm.includes("aliment") || norm.includes("comida")) return "/Iconos/Categorias/Alimnetacion.png";
  if (norm.includes("transporte") || norm.includes("carro") || norm.includes("moto") || norm.includes("uber")) return "/Iconos/Categorias/transporte.png";
  if (norm.includes("entretenimiento") || norm.includes("diversion") || norm.includes("cine")) return "/Iconos/Categorias/Entretenimiento.png";
  if (norm.includes("salud") || norm.includes("medica") || norm.includes("farmacia")) return "/Iconos/Categorias/salud.png";
  if (norm.includes("educac") || norm.includes("universidad") || norm.includes("colegio") || norm.includes("escuela") || norm.includes("curso")) return "/Iconos/Categorias/Educacion.png";
  if (norm.includes("hogar") || norm.includes("casa") || norm.includes("muebles")) return "/Iconos/Categorias/Hogar.png";
  if (norm.includes("servicios") || norm.includes("luz") || norm.includes("agua") || norm.includes("gas")) return "/Iconos/Categorias/Servicios.png";
  if (norm.includes("suscri") || norm.includes("netflix") || norm.includes("spotify")) return "/Iconos/Categorias/Suscripciones.png";
  if (norm.includes("viaje") || norm.includes("vuelo") || norm.includes("hotel")) return "/Iconos/Categorias/Viajes.png";
  if (norm.includes("deuda") || norm.includes("prestam") || norm.includes("banco")) return "/Iconos/Categorias/Deudas.png";
  if (norm.includes("ahorro") || norm.includes("meta") || norm.includes("piggy")) return "/Iconos/Categorias/Ahorros.png";
  if (norm.includes("deporte") || norm.includes("gym") || norm.includes("ejercicio") || norm.includes("futbol") || norm.includes("entreno")) return "/Iconos/Categorias/deporte.png";
  return "/Iconos/Categorias/otros.png";
}

function getCatIcon(icon, name="", size=24) {
  if (icon && typeof icon === "string" && (icon.startsWith("/") || icon.includes(".png"))) {
    return <img src={icon} alt={name || "category"} style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  const target = name || icon || "";
  if (target && typeof target === "string" && (target.startsWith("/") || target.includes(".png"))) {
    return <img src={target} alt={name || "category"} style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  const url = getCatIconUrl(target);
  return <img src={url} alt={target} style={{ width: size, height: size, objectFit: "contain" }} />;
}

export default function TripsPage() {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const { isExpired } = useOutletContext() || {};
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTrip, setActiveTrip] = useState(null);
  
  // Modals
  const [isNewTripOpen, setIsNewTripOpen] = useState(false);
  const [isClosingTrip, setIsClosingTrip] = useState(false);
  const [isEditTripOpen, setIsEditTripOpen] = useState(false);
  const [tripToEdit, setTripToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", budget: "", icon: "", currency: "COP" });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState("newest"); // newest | oldest
  const [createDateOpen, setCreateDateOpen] = useState(false);
  const [editDateOpen, setEditDateOpen] = useState(false);
  
  // Form Create
  const [formData, setFormData] = useState({ name: "", budget: "", icon: "", trip_date: "", currency: "COP" });

  // Travel history modal state
  const [historyTrip, setHistoryTrip] = useState(null);
  const [tripExpenses, setTripExpenses] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [hoveredCatHistory, setHoveredCatHistory] = useState(null);
  const [activeCatHistory, setActiveCatHistory] = useState(null);

  // Direct trip expense form
  const [expenseTrip, setExpenseTrip] = useState(null);
  const [tripCreditCards, setTripCreditCards] = useState([]);
  const [isSavingTripExpense, setIsSavingTripExpense] = useState(false);
  const [tripExpenseForm, setTripExpenseForm] = useState({
    amount: "",
    category: "",
    description: "",
    transaction_date: getLocalDateInputValue(),
  });

  // Filtered trips
  const filteredTrips = useMemo(() => {
    let result = [...trips];

    // Search filter
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(t => (t.name || "").toLowerCase().includes(lower));
    }

    // Date range filter
    if (dateRange.from && dateRange.to) {
      const from = new Date(dateRange.from); from.setHours(0,0,0,0);
      const to = new Date(dateRange.to); to.setHours(23,59,59,999);
      result = result.filter(t => {
        const d = new Date(t.trip_date || t.created_at);
        return d >= from && d <= to;
      });
    }

    // Sort by trip_date (fallback to created_at)
    result.sort((a, b) => {
      const da = new Date(a.trip_date || a.created_at);
      const db = new Date(b.trip_date || b.created_at);
      return sortOrder === "newest" ? db - da : da - db;
    });

    return result;
  }, [trips, searchTerm, sortOrder, dateRange]);

  const fetchTrips = async () => {
    try {
      setLoading(true);
      const res = await api.get("/trips");
      setTrips(res.data);
    } catch (err) {
      toast.error("Error al cargar los viajes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrips();
  }, []);

  useEffect(() => {
    if (searchParams.get("action") !== "add") return;
    if (!isExpired) {
      setFormData({ name: "", budget: "", icon: "", trip_date: "", currency: "COP" });
      setIsNewTripOpen(true);
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, isExpired, setSearchParams]);

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    if (!isFutureDateValue(formData.trip_date)) {
      toast.error("Selecciona una fecha futura para el viaje");
      return;
    }
    
    try {
      const payload = {
        name: formData.name,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        trip_date: formData.trip_date || null,
        icon: formData.icon || null,
        currency: formData.currency === "OTRA" ? "COP" : (formData.currency || "COP")
      };
      await api.post("/trips", payload);
      toast.success("Viaje creado exitosamente");
      setIsNewTripOpen(false);
      setFormData({ name: "", budget: "", icon: "", trip_date: "", currency: "COP" });
      fetchTrips();
    } catch (err) {
      toast.error("Error al crear viaje");
    }
  };

  const handleCloseTrip = async (id) => {
    if (!window.confirm("¿Cerrar este viaje? Conservarás su historial y sus movimientos. Tu balance no cambiará.")) return;
    try {
      setIsClosingTrip(true);
      await api.post(`/trips/${id}/close`);
      toast.success("Viaje cerrado. El historial y los movimientos se conservaron.");
      setActiveTrip(null);
      setIsEditTripOpen(false);
      setTripToEdit(null);
      fetchTrips();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cerrando viaje");
    } finally {
      setIsClosingTrip(false);
    }
  };

  const openEditModal = (trip) => {
    setTripToEdit(trip);
    
    const tripCurrency = trip.currency || "COP";
    
    setEditFormData({ 
      name: trip.name, 
      budget: trip.budget ? String(trip.budget) : "",
      icon: trip.icon || "", 
      trip_date: trip.trip_date || "", 
      currency: tripCurrency
    });
    setIsEditTripOpen(true);
  };

  const handleUpdateTrip = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim()) return;
    try {
      const payload = {
        name: editFormData.name,
        budget: editFormData.budget ? parseFloat(editFormData.budget) : null,
        icon: editFormData.icon || "🛩️",
        trip_date: editFormData.trip_date || null,
        currency: editFormData.currency === "OTRA" ? "COP" : (editFormData.currency || "COP")
      };
      await api.put(`/trips/${tripToEdit.id}`, payload);
      toast.success("Viaje actualizado");
      setIsEditTripOpen(false);
      setTripToEdit(null);
      fetchTrips();
    } catch (err) {
      toast.error("Error al actualizar viaje");
    }
  };

  const handleDeleteTrip = async (id) => {
    if (!window.confirm("¿Seguro que quieres eliminar este viaje por completo? Se borrarán todos los gastos asociados al viaje permanentemente.")) return;
    try {
      await api.delete(`/trips/${id}`);
      toast.success("Viaje eliminado");
      setIsEditTripOpen(false);
      setTripToEdit(null);
      if (activeTrip?.id === id) setActiveTrip(null);
      fetchTrips();
    } catch (err) {
      toast.error("Error al eliminar viaje");
    }
  };

  const loadTripTransactions = async (trip) => {
    setActiveTrip(trip);
    try {
      const res = await api.get(`/trips/${trip.id}/transactions`);
      setActiveTrip(prev => ({ ...prev, expenses: res.data }));
    } catch (err) {
      toast.error("No se pudieron cargar los detalles");
    }
  };

  const openTripHistory = (trip) => {
    navigate(`/dashboard/trips/${trip.id}/history`, { state: { trip } });
  };

  const closeTripExpenseModal = () => {
    setExpenseTrip(null);
    setTripCreditCards([]);
    setTripExpenseForm({ amount: "", category: "", credit_card_id: "", description: "", transaction_date: getLocalDateInputValue() });
  };

  const openTripExpenseModal = async (trip) => {
    if (isExpired || trip.status === "closed") return;

    setExpenseTrip(trip);
    setTripCreditCards([]);
    setTripExpenseForm({ amount: "", category: "", credit_card_id: "", description: "", transaction_date: getLocalDateInputValue() });
    try {
      const response = await api.get("/credit-cards");
      setTripCreditCards(response.data || []);
    } catch {
      toast.error("No se pudieron cargar las tarjetas. Puedes cerrar y volver a abrir el formulario para reintentar.");
    }
  };

  const handleCreateTripExpense = async (event) => {
    event.preventDefault();
    if (!expenseTrip || !tripExpenseForm.amount) {
      toast.error("Completa el valor del gasto");
      return;
    }
    if (Number(tripExpenseForm.amount) <= 0) {
      toast.error("El valor del gasto debe ser mayor que cero");
      return;
    }
    if (tripExpenseForm.transaction_date > getLocalDateInputValue()) {
      toast.error("La fecha del gasto no puede estar en el futuro");
      return;
    }

    setIsSavingTripExpense(true);
    try {
      await api.post(`/trips/${expenseTrip.id}/expenses`, {
        ...tripExpenseForm,
        amount: Number(tripExpenseForm.amount),
      });
      toast.success(`Gasto agregado a ${expenseTrip.name}`);
      closeTripExpenseModal();
      fetchTrips();
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo agregar el gasto al viaje");
    } finally {
      setIsSavingTripExpense(false);
    }
  };

  if (loading && trips.length === 0) {
    return (
      <div className="flex flex-col h-full bg-surface justify-center items-center">
        <Loader2 className="h-10 w-10 text-primary animate-spin" />
      </div>
    );
  }

  // INNER VIEW: TRIP DETAILS (We will redesign logic by categories in next step, keeping structure minimal for now)
  if (activeTrip) {
    const isClosed = activeTrip.status === "closed";
    const totalSpent = activeTrip.total_spent || 0;
    const budget = activeTrip.budget || 0;
    const progress = budget > 0 ? Math.min((totalSpent / budget) * 100, 100) : 0;
    const remaining = budget > 0 ? Math.max(0, budget - totalSpent) : totalSpent;

    // SVG Circle Math
    const radius = 40;
    const circumference = 2 * Math.PI * radius; // ~251.2
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    // Group expenses by category
    const categoryTotals = {};
    (activeTrip.expenses || []).forEach(exp => {
        const cat = exp.trip_expense_category || exp.category || "Otros";
        categoryTotals[cat] = (categoryTotals[cat] || 0) + Math.abs(exp.trip_amount ?? exp.amount);
    });

    const getCatStyles = (catName) => {
        const lower = catName.toLowerCase();
        if (lower.includes("vuelo") || lower.includes("transporte") || lower.includes("flight")) return { icon: "flight", bg: "bg-primary-container", textCls: "text-on-primary-container" };
        if (lower.includes("hotel") || lower.includes("hospedaje") || lower.includes("lodging") || lower.includes("bnb")) return { icon: "bed", bg: "bg-secondary-container", textCls: "text-on-secondary-container" };
        if (lower.includes("comida") || lower.includes("restaurante") || lower.includes("dining")) return { icon: "restaurant", bg: "bg-tertiary-container", textCls: "text-on-tertiary-container" };
        return { icon: "more_horiz", bg: "bg-surface-container-high", textCls: "text-on-surface" };
    };

    return (
      <div className="flex-1 overflow-y-auto pb-24 bg-surface transition-colors duration-300">
        
        <header className="sticky top-0 w-full z-40 bg-surface/80 backdrop-blur-xl border-b border-surface-container-high/30">
          <div className="flex items-center gap-4 px-6 md:px-8 py-5 max-w-[1400px] mx-auto">
            <button 
              onClick={() => setActiveTrip(null)}
              className="text-primary hover:opacity-80 transition-opacity active:scale-90"
            >
              <span className="material-symbols-outlined text-2xl" style={{ fontVariationSettings: "'FILL' 1" }}>arrow_back</span>
            </button>
            <h1 className="font-headline font-bold text-2xl tracking-tight text-primary">{activeTrip.icon || "🛩️"} {activeTrip.name}</h1>
            
            {/* Close Trip Action */}
            {!isClosed && (
              <div className="ml-auto">
                <button 
                  onClick={() => handleCloseTrip(activeTrip.id)}
                  disabled={isClosingTrip}
                  className="flex items-center gap-2 px-4 py-2 bg-error/10 text-error hover:bg-error/20 active:scale-95 transition-all rounded-full font-bold text-sm"
                >
                  {isClosingTrip ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>}
                  <span className="hidden sm:inline">Finalizar Viaje</span>
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="pt-8 pb-12 px-6 md:px-8 max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Left Column: Budget Overview */}
            <section className="col-span-1 lg:col-span-4 flex flex-col gap-8">
              <div className="bg-surface-container-lowest rounded-[24px] p-8 flex flex-col items-center justify-between shadow-[0_8px_24px_rgba(43,52,53,0.04)] h-full min-h-[500px] transition-shadow hover:shadow-lg">
                <div className="w-full">
                  <div className="flex items-center gap-3 mb-12">
                    <div className="p-2.5 bg-primary-container/40 rounded-xl">
                      <span className="text-xl">💰</span>
                    </div>
                    <h2 className="text-xl font-headline font-extrabold tracking-tight text-on-surface">Resumen de Presupuesto</h2>
                  </div>
                  
                  <div className="relative w-64 h-64 mx-auto mb-14 drop-shadow-sm">
                    {/* SVG Circular Progress */}
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                      <circle className="text-surface-container" cx="50" cy="50" fill="transparent" r="40" stroke="currentColor" strokeWidth="8"></circle>
                      <circle 
                        className={progress > 90 ? "text-error" : progress > 70 ? "text-tertiary" : "text-primary"}
                        cx="50" cy="50" fill="transparent" r="40" stroke="currentColor" 
                        strokeDasharray={Object.is(circumference, NaN) ? 0 : circumference} 
                        strokeDashoffset={Object.is(strokeDashoffset, NaN) ? 0 : strokeDashoffset} 
                        strokeLinecap="round" strokeWidth="8"
                        style={{ transition: "stroke-dashoffset 1.5s ease-in-out" }}
                      ></circle>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                      <span className={`font-headline font-extrabold text-on-surface ${remaining.toString().length > 6 ? 'text-2xl' : 'text-3xl'}`}>{activeTrip.currency || 'COP'} {remaining.toLocaleString()}</span>
                      <span className="text-[11px] font-label font-bold text-outline tracking-widest uppercase mt-1">
                        {budget > 0 ? "Restante" : "Total Gastado"}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-4 w-full">
                    <div className="flex justify-between items-end p-5 rounded-2xl bg-surface-container-low transition-colors hover:bg-surface-container-highest">
                      <div>
                        <p className="text-[11px] font-label font-bold text-outline uppercase tracking-widest mb-1.5">Total Gastado</p>
                        <p className="text-2xl font-headline font-extrabold text-on-surface">{activeTrip.currency || 'COP'} {totalSpent.toLocaleString()}</p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        {budget > 0 && (
                           <p className="text-[10px] sm:text-[11px] font-label font-bold text-on-primary-container bg-primary-container px-2 py-0.5 rounded-full inline-block whitespace-nowrap">
                             {progress.toFixed(0)}% del Total
                           </p>
                        )}
                      </div>
                    </div>
                    {budget > 0 && (
                      <div className="flex justify-between items-end p-5 rounded-2xl bg-surface-container-low transition-colors hover:bg-surface-container-highest">
                        <div>
                          <p className="text-[11px] font-label font-bold text-outline uppercase tracking-widest mb-1.5">Presupuesto Total</p>
                          <p className="text-2xl font-headline font-extrabold text-on-surface">{activeTrip.currency || 'COP'} {budget.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <span className="material-symbols-outlined text-outline" style={{ fontVariationSettings: "'FILL' 1" }}>info</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Right Column */}
            <div className="col-span-1 lg:col-span-8 flex flex-col gap-8">
              
              {/* Expenses by Category */}
              <section className="bg-surface-container-lowest rounded-[24px] p-8 shadow-[0_8px_24px_rgba(43,52,53,0.04)] transition-shadow hover:shadow-lg">
                <div className="flex items-center gap-3 mb-8">
                  <div className="p-2.5 bg-tertiary-container/30 rounded-xl">
                    <span className="text-xl">📈</span>
                  </div>
                  <h2 className="text-xl font-headline font-extrabold tracking-tight text-on-surface">Gastos por Categoría</h2>
                </div>
                
                {Object.keys(categoryTotals).length === 0 ? (
                    <div className="text-center font-medium text-on-surface-variant py-8">
                       Aún no hay gastos para clasificar. Escríbele al bot para registrar algo.
                    </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                    {Object.entries(categoryTotals)
                      .sort((a,b) => b[1] - a[1]) // Sort descending
                      .map(([catName, amount], idx) => {
                        const style = getCatStyles(catName);
                        const pct = totalSpent > 0 ? (amount / totalSpent) * 100 : 0;
                        const fillColors = ["bg-primary", "bg-secondary", "bg-tertiary", "bg-primary-fixed-dim"];
                        const fillColor = fillColors[idx % fillColors.length];
                        
                        return (
                          <div key={catName} className="space-y-3">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-3">
                                <div className={`w-11 h-11 rounded-xl ${style.bg} flex items-center justify-center`}>
                                  <span className={`material-symbols-outlined ${style.textCls}`} style={{ fontVariationSettings: "'FILL' 1" }}>{style.icon}</span>
                                </div>
                                <span className="font-headline font-bold text-on-surface">{catName}</span>
                              </div>
                              <span className="font-headline font-extrabold text-on-surface">{activeTrip.currency || 'COP'} {amount.toLocaleString()}</span>
                            </div>
                            <div className="h-2 w-full bg-surface-container-low rounded-full overflow-hidden">
                              <div className={`h-full ${fillColor} rounded-full transition-all duration-1000`} style={{ width: `${pct}%` }}></div>
                            </div>
                          </div>
                      )
                    })}
                  </div>
                )}
              </section>

              {/* Transaction History */}
              <section className="bg-surface-container-lowest rounded-[24px] p-8 shadow-[0_8px_24px_rgba(43,52,53,0.04)] transition-shadow hover:shadow-lg">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-secondary-container/30 rounded-xl">
                      <span className="text-xl">📜</span>
                    </div>
                    <h2 className="text-xl font-headline font-extrabold tracking-tight text-on-surface">Historial de Transacciones</h2>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[500px]">
                    <thead>
                      <tr className="text-[11px] font-label font-bold text-outline uppercase tracking-widest border-b border-surface-container-highest">
                        <th className="pb-5 pl-2">Fecha</th>
                        <th className="pb-5">Categoría</th>
                        <th className="pb-5">Descripción</th>
                        <th className="pb-5 pr-2 text-right">Monto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-container-low">
                      {!activeTrip.expenses || activeTrip.expenses.length === 0 ? (
                        <tr>
                            <td colSpan="4" className="py-8 text-center text-on-surface-variant font-medium">
                                No se ha registrado ninguna actividad reciente.
                            </td>
                        </tr>
                      ) : (
                        activeTrip.expenses.map((exp, idx) => {
                          const dateStr = new Date(exp.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric', year: 'numeric' });
                          const style = getCatStyles(exp.trip_expense_category || exp.category || "Otros");
                          
                          return (
                            <tr key={idx} className="group hover:bg-surface-container-low/50 transition-colors">
                              <td className="py-5 pl-2 text-sm font-medium text-outline whitespace-nowrap">{dateStr}</td>
                              <td className="py-5">
                                <div className="flex items-center gap-3">
                                  <div className={`w-9 h-9 rounded-xl ${style.bg}/40 flex items-center justify-center`}>
                                    <span className={`material-symbols-outlined ${style.textCls} text-sm`} style={{ fontVariationSettings: "'FILL' 1" }}>{style.icon}</span>
                                  </div>
                                  <span className="text-sm font-bold text-on-surface">{exp.trip_expense_category || exp.category || "Otros"}</span>
                                </div>
                              </td>
                              <td className="py-5 text-sm font-headline font-bold text-on-surface">{exp.description || "-"}</td>
                              <td className="py-5 pr-2 text-right font-headline font-extrabold text-error">
                                -{activeTrip.currency || 'COP'} {Math.abs(exp.trip_amount ?? exp.amount).toLocaleString()}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

            </div>
          </div>
        </main>
      </div>
    );
  }

  // Calculate top level stats
  const activeTripsCount = trips.filter(t => t.status === "active").length;
  const totalTripsCount = trips.length;
  const totalTripsSpent = trips.reduce((sum, t) => sum + (t.total_spent || 0), 0);

  // Mapped Emojis for trips loosely based on their index
  const emojiMap = ["🇯🇵", "🇮🇹", "🏔️", "🇬🇧", "🏖️", "🗽", "🏜️"];
  const colorMap = ["bg-primary-container/20", "bg-secondary-container/20", "bg-tertiary-container/20", "bg-primary-fixed-dim/20"];
  const textColors = ["text-primary", "text-secondary", "text-tertiary", "text-on-primary-fixed"];
  const bgColors = ["bg-primary", "bg-secondary", "bg-tertiary", "bg-primary-fixed"];
  const hoverBgs = ["hover:bg-primary-container/20", "hover:bg-secondary-container/20", "hover:bg-tertiary-container/20", "hover:bg-primary-fixed/20"];

  // MAIN VIEW: LIST OF TRIPS
  return (
    <div className="pb-32 md:pb-12 max-w-7xl mx-auto w-full" style={{
      display: "flex",
      flexDirection: "column",
      gap: "24px",
    }}>
      
      {/* ─── Title ─── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
        <h1 style={{
          fontFamily: "'Manrope', sans-serif",
          fontWeight: 700,
          fontSize: 24,
          lineHeight: "33px",
          color: "#255E99",
          margin: 0,
        }}>Mis viajes</h1>
      </div>

      {trips.length === 0 ? (
        <>
          {/* Top Banner Card */}
          <div style={{
            position: "relative",
            width: "100%",
            minHeight: "123px",
            background: "#FFFFFF",
            boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: "clamp(16px, 4vw, 20px) clamp(16px, 4vw, 30px) clamp(16px, 4vw, 20px) clamp(120px, 35vw, 180px)",
            boxSizing: "border-box",
            marginTop: "16px",
          }}>
            {/* Overflowing Globe Illustration */}
            <div style={{
              position: "absolute",
              left: "clamp(-10px, 2vw, 20px)",
              top: "clamp(-10px, -2vw, -22px)",
              width: "clamp(110px, 28vw, 139.26px)",
              height: "clamp(110px, 28vw, 139.26px)",
              borderRadius: "50%",
              background: "#F0FDFD",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <img 
                src="/Iconos/Viajes/icono_viaje_header.png" 
                alt="Plane globe" 
                style={{ width: "90%", height: "90%", objectFit: "contain" }} 
              />
            </div>
            {/* Banner Text */}
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", zIndex: 10 }}>
              <h2 style={{
                margin: 0,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: "clamp(18px, 5vw, 32px)",
                lineHeight: "1.2",
                color: "#47484C",
              }}>¡Es hora de planear tu nuevo viaje!</h2>
              <p style={{
                margin: 0,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 400,
                fontSize: "clamp(14px, 3.5vw, 18px)",
                lineHeight: "1.3",
                color: "#47484C",
              }}>Con aorra lo puedes lograr</p>
            </div>
          </div>

          {/* Large Empty State Card */}
          <div style={{
            width: "100%",
            height: "590px",
            background: "#FFFFFF",
            borderRadius: "20px",
            boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px",
            boxSizing: "border-box",
          }}>
            <svg viewBox="319 99 610 322" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "348px", height: "auto", marginBottom: "24px" }}>
              <path d="M655.608 181.119C666.541 181.119 675.404 189.988 675.404 200.929C675.404 211.869 666.541 220.739 655.608 220.739H836.601C847.533 220.739 856.396 229.608 856.396 240.549C856.396 251.489 847.533 260.359 836.601 260.359H898.816C909.749 260.359 918.612 269.228 918.612 280.169C918.612 291.11 909.749 299.979 898.816 299.979H845.084C834.151 299.98 825.288 308.848 825.288 319.789C825.288 330.73 834.151 339.6 845.084 339.6H862.052C872.985 339.6 881.849 348.468 881.849 359.409C881.849 370.35 872.985 379.219 862.052 379.219H714.996C713.54 379.219 712.12 379.062 710.754 378.764C709.387 379.062 707.968 379.219 706.512 379.219H449.164C438.231 379.219 429.368 370.35 429.368 359.409C429.368 348.468 438.231 339.6 449.164 339.6H338.872C327.939 339.6 319.076 330.73 319.076 319.789C319.076 308.848 327.939 299.98 338.872 299.979H451.992C462.925 299.979 471.788 291.11 471.788 280.169C471.788 269.228 462.925 260.359 451.992 260.359H381.292C370.359 260.359 361.496 251.49 361.496 240.549C361.496 229.608 370.359 220.739 381.292 220.739H494.412C483.479 220.739 474.616 211.869 474.616 200.929C474.616 189.988 483.479 181.119 494.412 181.119H655.608ZM910.128 339.6C921.061 339.6 929.924 348.468 929.924 359.409C929.924 370.35 921.061 379.219 910.128 379.219C899.195 379.219 890.332 370.35 890.332 359.409C890.332 348.468 899.195 339.6 910.128 339.6Z" fill="#F3F7FF"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M483.682 240.758H481.005C463.881 240.758 450 226.917 450 209.842C450 192.768 463.881 178.927 481.005 178.927C481.115 178.927 481.226 178.927 481.336 178.929C484.092 154.982 503.715 136.418 527.512 136.418C552.594 136.418 573.039 157.041 573.985 182.852C589.151 183.83 602.496 198.57 602.496 213.939C602.496 226.558 593.137 240.758 582.05 240.758H504.95" fill="white"/>
              <path d="M483.682 240.758H481.005C463.881 240.758 450 226.917 450 209.842C450 192.768 463.881 178.927 481.005 178.927C481.115 178.927 481.226 178.927 481.336 178.929C484.092 154.982 503.715 136.418 527.512 136.418C552.594 136.418 573.039 157.041 573.985 182.852C589.151 183.83 602.496 198.57 602.496 213.939C602.496 226.558 593.137 240.758 582.05 240.758H504.95" stroke="#75A4FE" strokeWidth="2.5" strokeLinecap="round"/>
              <path d="M645.298 109.469C699.175 109.469 742.888 153.913 742.888 208.782C742.888 225.873 735.727 247.833 721.207 274.729C706.701 301.598 684.919 333.272 655.836 369.757C655.242 370.502 654.573 371.181 653.842 371.785C648.025 376.585 639.486 375.684 634.761 369.757C605.677 333.272 583.895 301.598 569.39 274.729C554.869 247.833 547.708 225.873 547.708 208.782C547.708 153.913 591.421 109.469 645.298 109.469Z" fill="white" stroke="#1F64E7" strokeWidth="2.5"/>
              <path d="M679.943 129.965C709.403 143.635 730.018 174.209 730.018 209.923C730.017 224.368 723.959 244.111 710.354 269.671C696.857 295.027 676.434 325.192 648.899 360.225C648.687 360.494 648.451 360.737 648.195 360.951C646.225 362.6 643.35 362.33 641.695 360.225C614.161 325.192 593.739 295.027 580.242 269.671C577.07 263.711 574.31 258.067 571.939 252.733C583.82 247.522 595.074 241.15 605.562 233.766C613.434 247.735 628.279 257.152 645.298 257.152C670.549 257.152 691.019 236.42 691.02 210.847C691.02 191.995 679.895 175.775 663.933 168.551C670.595 156.421 675.991 143.501 679.943 129.965Z" fill="#E8F0FE"/>
              <path d="M596.665 363.25C597.345 363.129 597.797 362.479 597.676 361.8C597.554 361.12 596.905 360.668 596.225 360.789L596.445 362.02L596.665 363.25ZM576.786 365.328C576.125 365.526 575.749 366.222 575.947 366.883C576.144 367.545 576.841 367.921 577.502 367.723L577.144 366.526L576.786 365.328ZM565.904 371.871C566.539 371.598 566.832 370.863 566.559 370.229C566.286 369.594 565.551 369.301 564.917 369.574L565.411 370.722L565.904 371.871ZM695.846 361.058C695.167 360.932 694.515 361.38 694.388 362.058C694.262 362.737 694.71 363.39 695.389 363.516L695.617 362.287L695.846 361.058ZM596.445 362.02L596.225 360.789C589.17 362.05 582.645 363.577 576.786 365.328L577.144 366.526L577.502 367.723C583.257 366.003 589.689 364.497 596.665 363.25L596.445 362.02ZM565.411 370.722L564.917 369.574C558.884 372.168 554 375.098 550.606 378.309C547.21 381.522 545.208 385.114 545.208 388.992H546.458H547.708C547.708 386.039 549.223 383.059 552.324 380.125C555.426 377.191 560.019 374.401 565.904 371.871L565.411 370.722ZM546.458 388.992H545.208C545.208 393.855 548.346 398.261 553.48 402.07C558.638 405.897 566.022 409.291 575.033 412.119C593.071 417.78 617.913 421.262 645.298 421.262V420.012V418.762C618.095 418.762 593.517 415.3 575.782 409.734C566.907 406.948 559.816 403.658 554.97 400.062C550.101 396.449 547.708 392.694 547.708 388.992H546.458ZM645.298 420.012V421.262C672.683 421.262 697.525 417.78 715.563 412.119C724.574 409.291 731.958 405.897 737.116 402.07C742.25 398.261 745.388 393.855 745.388 388.992H744.138H742.888C742.888 392.694 740.495 396.449 735.626 400.062C730.78 403.658 723.689 406.948 714.814 409.734C697.079 415.3 672.501 418.762 645.298 418.762V420.012ZM744.138 388.992H745.388C745.388 382.556 739.918 376.913 731.363 372.303C722.706 367.638 710.436 363.772 695.846 361.058L695.617 362.287L695.389 363.516C709.845 366.205 721.835 370.009 730.177 374.504C738.621 379.055 742.888 384.063 742.888 388.992H744.138Z" fill="#1F64E7"/>
              <path d="M645.298 163.051C671.123 163.051 692.056 183.957 692.056 209.741C692.055 235.526 671.123 256.431 645.298 256.431C619.473 256.431 598.54 235.525 598.54 209.741C598.54 183.957 619.473 163.051 645.298 163.051Z" stroke="#1F64E7" strokeWidth="2.5"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M763.523 130.532C762.608 128.247 760.794 126.437 758.506 125.523L746.962 120.911L758.506 116.298C760.794 115.384 762.608 113.574 763.523 111.289L768.142 99.7606L772.76 111.289C773.676 113.574 775.489 115.384 777.777 116.298L789.322 120.911L777.777 125.523C775.489 126.437 773.676 128.247 772.76 130.532L768.142 142.061L763.523 130.532Z" stroke="#75A4FE" strokeWidth="2.5" strokeLinejoin="round"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M709.376 336.64H707.426C694.949 336.64 684.834 326.54 684.834 314.08C684.834 301.62 694.949 291.52 707.426 291.52C707.507 291.52 707.587 291.52 707.667 291.521C709.676 274.047 723.974 260.5 741.314 260.5C759.59 260.5 774.487 275.549 775.177 294.385C786.228 295.098 795.952 305.854 795.952 317.069C795.952 326.278 789.132 336.64 781.053 336.64H724.874" fill="white"/>
              <path d="M709.376 336.64H707.426C694.949 336.64 684.834 326.54 684.834 314.08C684.834 301.62 694.949 291.52 707.426 291.52C707.507 291.52 707.587 291.52 707.667 291.521C709.676 274.047 723.974 260.5 741.314 260.5C759.59 260.5 774.487 275.549 775.177 294.385C786.228 295.098 795.952 305.854 795.952 317.069C795.952 326.278 789.132 336.64 781.053 336.64H724.874" stroke="#75A4FE" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            <h3 style={{
              margin: "0 0 8px 0",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "27px",
              color: "#4A4A4A",
              textAlign: "center",
            }}>Sin resultados</h3>
            <p style={{
              margin: "0 0 30px 0",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 400,
              fontSize: "16px",
              color: "#687377",
              textAlign: "center",
            }}>No tienes viajes registrados</p>
            
            <button
              onClick={() => {
                if (isExpired) return;
                setIsNewTripOpen(true);
              }}
              disabled={isExpired}
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                padding: "0px 24px",
                gap: "8px",
                width: "259px",
                height: "54px",
                background: "#396BE9",
                borderRadius: "16px",
                border: "none",
                cursor: "pointer",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: "16px",
                color: "#FFFFFF",
                transition: "opacity 0.15s ease",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = 0.9}
              onMouseLeave={e => e.currentTarget.style.opacity = 1}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              Agregar nuevo viaje
            </button>
          </div>
        </>
      ) : (
        <>
          {/* ─── KPI Cards ─── */}
          {/* Mobile: "Has gastado" full-width first, then 2-col row for the other two. Desktop: 3-col. */}
          <div className="aorra-trips-kpi-outer" style={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>

            {/* [1] Has gastado en viajes — full row */}
            <div style={{
              boxSizing: "border-box",
              display: "flex", flexDirection: "row", alignItems: "center",
              padding: "9px 30px", gap: "30px", height: "123px",
              background: "#FFFFFF", boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
              borderRadius: "20px", width: "100%",
            }}>
              <div style={{
                width: "80px", height: "80px", borderRadius: "50%", background: "#F0FDFD",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <img src="/Iconos/Viajes/maletas.png" alt="Gastos" style={{ width: "49.23px", height: "49.23px", objectFit: "contain" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", flex: 1, gap: "4px" }}>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "18px", lineHeight: "25px", color: "#47484C" }}>Has gastado en viajes</span>
                <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "24px", lineHeight: "32px", color: "#255E99" }}>${totalTripsSpent.toLocaleString()}</span>
              </div>
            </div>

            {/* [2+3] Viajes activos + Viajes y planes — 2 columns */}
            <div className="aorra-trips-kpi-row2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", width: "100%" }}>

              {/* Viajes activos */}
              <div style={{
                boxSizing: "border-box",
                display: "flex", flexDirection: "row", alignItems: "center",
                padding: "9px 20px", gap: "16px", height: "123px",
                background: "#FFFFFF", boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
                borderRadius: "20px",
              }}>
                <div style={{
                  width: "60px", height: "60px", borderRadius: "50%", background: "#F0FDFD",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <img src="/Iconos/Viajes/pasaporte.png" alt="Viajes activos" style={{ width: "38px", height: "38px", objectFit: "contain" }} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", flex: 1, gap: "4px", minWidth: 0 }}>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "14px", lineHeight: "19px", color: "#47484C" }}>Viajes activos</span>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "22px", lineHeight: "30px", color: "#255E99" }}>{activeTripsCount}</span>
                </div>
              </div>

              {/* Viajes y planes */}
              <div style={{
                boxSizing: "border-box",
                display: "flex", flexDirection: "row", alignItems: "center",
                padding: "9px 20px", gap: "16px", height: "123px",
                background: "#FFFFFF", boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
                borderRadius: "20px",
              }}>
                <div style={{
                  width: "60px", height: "60px", borderRadius: "50%", background: "#F0FDFD",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <img src="/Iconos/Viajes/maletas.png" alt="Total viajes" style={{ width: "38px", height: "38px", objectFit: "contain" }} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", flex: 1, gap: "4px", minWidth: 0 }}>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "14px", lineHeight: "19px", color: "#47484C" }}>Viajes y planes</span>
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "22px", lineHeight: "30px", color: "#255E99" }}>{totalTripsCount}</span>
                </div>
              </div>

            </div>
          </div>


          {/* ─── Search & Filter Row ─── */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", width: "100%", marginTop: "10px" }}>
            {/* Search */}
            <div style={{ position: "relative", flex: "1 1 260px", height: "40px" }}>
              <Search style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#ADADAD", width: 18, height: 18 }} />
              <input
                placeholder="Buscar viajes..."
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  boxSizing: "border-box", width: "100%", height: "40px",
                  paddingLeft: "42px", paddingRight: "16px",
                  background: "#FFFFFF", border: "1px solid #CDE3F3",
                  borderRadius: "12px", fontFamily: "'Manrope', sans-serif",
                  fontWeight: 600, fontSize: "14px", color: "#4A4A4A", outline: "none",
                }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {/* Date range chip */}
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <button style={{
                    display: "flex", alignItems: "center", padding: "0 14px 0 8px",
                    gap: 8, height: 40, minWidth: 160,
                    background: "#E1E9FC", borderRadius: 24,
                    border: "none", cursor: "pointer", transition: "opacity 0.15s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 0.9}
                    onMouseLeave={e => e.currentTarget.style.opacity = 1}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: 100,
                      background: "#4D72F5", display: "flex",
                      alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM19 7H5V6H19V7Z" fill="#FFFFFF"/>
                      </svg>
                    </div>
                    <span style={{
                      fontFamily: "'Manrope', sans-serif", fontWeight: 600,
                      fontSize: 13, color: "#396BE9", whiteSpace: "nowrap",
                    }}>
                      {dateRange.from && dateRange.to
                        ? `${dateRange.from.toLocaleDateString("es-CO",{day:"numeric",month:"short"})} - ${dateRange.to.toLocaleDateString("es-CO",{day:"numeric",month:"short"})}`
                        : "Rango de fechas"}
                    </span>
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                      <path d="M1 1L5 5L9 1" stroke="#396BE9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-3xl shadow-2xl border-0 overflow-hidden" align="end"
                  style={{ background: "rgba(255,255,255,0.95)", backdropFilter: "blur(24px)", zIndex: 150 }}>
                  <div style={{ padding: 12 }}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 12, borderBottom: "1px solid #f0f0f0", paddingBottom: 8 }}>
                      <button onClick={() => { setDateRange({ from: null, to: null }); setDatePickerOpen(false); }}
                        style={{ padding: "6px 12px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
                          background: !dateRange.from ? "#396BE9" : "#f5f5f5", color: !dateRange.from ? "#fff" : "#396BE9" }}>
                        Todos
                      </button>
                    </div>
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(r) => {
                        setDateRange(r || { from: null, to: null });
                        if (r?.from && r?.to) setDatePickerOpen(false);
                      }}
                      numberOfMonths={1}
                    />
                  </div>
                </PopoverContent>
              </Popover>

              {/* Sort popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <button style={{
                    height: 40, padding: "4px 14px",
                    background: "transparent", borderRadius: 16,
                    border: "none", cursor: "pointer",
                    fontFamily: "'Manrope', sans-serif", fontWeight: 700,
                    fontSize: 14, color: "#5C5C5C",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <ArrowUpDown style={{ width: 16, height: 16 }} />
                    Ordenar
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" style={{
                  background: "#FFFFFF", borderRadius: 20, padding: 20,
                  boxShadow: "-2px 0px 20px rgba(176,179,189,0.25)",
                  border: "1px solid #E0EDF8",
                  display: "flex", flexDirection: "column", gap: 6,
                  width: 220, zIndex: 150,
                }}>
                  <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 14, color: "#5C5C5C", marginBottom: 4 }}>Ordenar por</label>
                  {[
                    { value: "newest", label: "Más reciente" },
                    { value: "oldest", label: "Más antiguo" },
                  ].map(opt => (
                    <button key={opt.value} onClick={() => setSortOrder(opt.value)}
                      style={{
                        padding: "8px 14px", borderRadius: 10, border: "none", cursor: "pointer",
                        fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: 14,
                        textAlign: "left", width: "100%",
                        background: sortOrder === opt.value ? "#E1E9FC" : "transparent",
                        color: sortOrder === opt.value ? "#396BE9" : "#4A4A4A",
                        transition: "all 0.15s",
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* ─── Grid list of trips ─── */}
          <div className="aorra-trips-cards-grid" style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "24px",
            width: "100%",
            boxSizing: "border-box",
          }}>
            {filteredTrips.map((trip, idx) => {
              const spent = trip.total_spent || 0;
              const bgt = trip.budget || 0;
              const progress = bgt ? Math.min((spent / bgt) * 100, 100) : 0;
              const dateStr = new Date(trip.trip_date || trip.created_at).toLocaleDateString('es-CO', { day: 'numeric', month: 'long' });

              // Map icon string (emoji or PNG path)
              const tripIcon = trip.icon || "🏖️";

              return (
                <div key={trip.id} style={{
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "24px 19px 20px",
                  gap: "23px",
                  width: "100%",
                  minHeight: "415px",
                  background: "#FFFFFF",
                  boxShadow: "2px 8px 20px rgba(32, 85, 95, 0.2)",
                  borderRadius: "16px",
                  position: "relative",
                  margin: "0 auto",
                }}>
                  {/* Header: shape + title details */}
                  <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "0px",
                    gap: "8px",
                    width: "100%",
                    height: "116px",
                  }}>
                    <div style={{
                      width: "116px",
                      height: "116px",
                      background: "#F0FDFD",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      {tripIcon.startsWith("/") ? (
                        <img src={tripIcon} alt={trip.name} style={{ width: 56, height: 56, objectFit: "contain" }} />
                      ) : (
                        <span style={{ fontSize: 56 }}>{tripIcon}</span>
                      )}
                    </div>

                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      padding: "0px",
                      flex: 1,
                    }}>
                      <span style={{
                        padding: "3px 10px",
                        background: trip.status === "closed" ? "#FFF0F0" : "#E1E9FC",
                        color: trip.status === "closed" ? "#E74C3C" : "#396BE9",
                        borderRadius: "12px",
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 700,
                        fontSize: "11px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        marginBottom: "4px"
                      }}>
                        {trip.status === "closed" ? "Viaje cerrado" : "Viaje activo"}
                      </span>
                      <h4 style={{
                        margin: 0,
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 800,
                        fontSize: "28px",
                        lineHeight: "38px",
                        color: "#4A4A4A",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "200px",
                      }} title={trip.name}>{trip.name}</h4>
                      <span style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "19px",
                        color: "#687377",
                      }}>Fecha de creación {dateStr}</span>
                    </div>
                  </div>

                  {/* Edit button */}
                  <button 
                    type="button"
                    onClick={() => openEditModal(trip)}
                    style={{
                      position: "absolute",
                      right: "15px",
                      top: "15px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "29px",
                      height: "29px",
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#396BE9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </button>

                  {/* Savings Goal Box */}
                  <div style={{
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "0px 20px",
                    justifyContent: "space-between",
                    width: "100%",
                    height: "53px",
                    border: "2px solid #CDF7F7",
                    borderRadius: "16px",
                  }}>
                    <span style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 700,
                      fontSize: "16px",
                      color: "#5C5C5C",
                    }}>{bgt > 0 ? "Presupuesto" : "Sin presupuesto establecido"}</span>
                    {bgt > 0 && (
                      <span style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 800,
                        fontSize: "20px",
                        color: "#4A4A4A",
                      }}>${bgt.toLocaleString()}</span>
                    )}
                  </div>

                  {/* Progress part */}
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    padding: "0px",
                    gap: "9px",
                    width: "100%",
                  }}>
                    <div style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}>
                      <span style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 700,
                        fontSize: "14px",
                        color: "#4A4A4A",
                      }}>${spent.toLocaleString()} gastados</span>
                      {bgt > 0 && (
                        <span style={{
                          fontFamily: "'Manrope', sans-serif",
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "#687377",
                        }}>{progress.toFixed(0)}%</span>
                      )}
                    </div>
                    <div style={{
                      position: "relative",
                      width: "100%",
                      height: "6px",
                      background: "#E1E9FC",
                      borderRadius: "3px",
                      overflow: "hidden",
                    }}>
                      <div style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: `${bgt > 0 ? progress : 100}%`,
                        background: bgt > 0 ? "#82FFC3" : "#CDE3F3",
                        borderRadius: "3px",
                        transition: "width 0.4s ease",
                      }} />
                    </div>
                  </div>

                  {/* Button "Ver historial" */}
                  <button
                    onClick={() => openTripHistory(trip)}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "0px 24px",
                      width: "100%",
                      height: "42px",
                      background: "#396BE9",
                      borderRadius: "16px",
                      border: "none",
                      cursor: "pointer",
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 600,
                      fontSize: "15px",
                      color: "#FFFFFF",
                      transition: "opacity 0.15s ease",
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 0.9}
                    onMouseLeave={e => e.currentTarget.style.opacity = 1}
                  >
                    Ver historial
                  </button>
                  {trip.status !== "closed" && (
                    <button
                      type="button"
                      onClick={() => openTripExpenseModal(trip)}
                      disabled={isExpired}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "7px",
                        padding: "0px 24px",
                        width: "100%",
                        height: "42px",
                        background: "#E1E9FC",
                        borderRadius: "16px",
                        border: "none",
                        cursor: isExpired ? "not-allowed" : "pointer",
                        opacity: isExpired ? 0.55 : 1,
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 700,
                        fontSize: "15px",
                        color: "#396BE9",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={e => { if (!isExpired) e.currentTarget.style.background = "#D4DFFA"; }}
                      onMouseLeave={e => { if (!isExpired) e.currentTarget.style.background = "#E1E9FC"; }}
                    >
                      <Plus size={17} strokeWidth={2.5} /> Agregar gasto
                    </button>
                  )}
                </div>
              );
            })}

            {/* Add Card (Dashed outline card to create new trip) */}
            <div
              onClick={() => setIsNewTripOpen(true)}
              style={{
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "24px 19px 20px",
                gap: "20px",
                width: "100%",
                height: "361px",
                background: "transparent",
                border: "2px dashed rgba(57, 107, 233, 0.4)",
                borderRadius: "16px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                margin: "0 auto",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = "#FFFFFF";
                e.currentTarget.style.borderColor = "#396BE9";
                e.currentTarget.style.boxShadow = "2px 8px 20px rgba(32, 85, 95, 0.15)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "rgba(57, 107, 233, 0.4)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div style={{
                width: "80px",
                height: "80px",
                background: "#E1E9FC",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#396BE9" strokeWidth="3" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </div>
              <div style={{ textAlign: "center" }}>
                <p style={{
                  margin: 0,
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 800,
                  fontSize: "18px",
                  color: "#4A4A4A",
                }}>Crea un nuevo viaje</p>
                <p style={{
                  margin: "4px 0 0 0",
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 500,
                  fontSize: "14px",
                  color: "#687377",
                }}>Haz clic para comenzar</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Direct trip expense modal */}
      {expenseTrip && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}
            onClick={closeTripExpenseModal}
          />
          <div style={{
            position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            zIndex: 9999, width: 522, maxWidth: "calc(100vw - 32px)", maxHeight: "calc(100vh - 32px)", overflowY: "auto",
            padding: "40px 30px 24px", background: "#FFFFFF", borderRadius: 20,
            boxShadow: "-2px 0px 20px rgba(176,179,189,0.25)", boxSizing: "border-box",
          }}>
            <button
              type="button"
              aria-label="Cerrar formulario de gasto"
              onClick={closeTripExpenseModal}
              style={{ position: "absolute", top: 16, right: 18, width: 24, height: 24, background: "none", border: "none", cursor: "pointer", color: "#255E99", fontSize: 25, lineHeight: "20px" }}
            >×</button>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <h2 style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 24, lineHeight: "33px", color: "#1B2559" }}>Agregar gasto</h2>
              <p style={{ margin: "8px 0 0", fontFamily: "'Manrope', sans-serif", fontSize: 14, color: "#687377" }}>Se verá en <strong>{expenseTrip.name}</strong> y en Movimientos.</p>
            </div>
            <form onSubmit={handleCreateTripExpense} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={tripLabelStyle}>Valor del gasto</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", fontFamily: "'Manrope', sans-serif", color: "#5C5C5C", fontWeight: 700 }}>{expenseTrip.currency || "COP"}</span>
                  <input type="number" min="0.01" step="any" required autoFocus placeholder="0"
                    value={tripExpenseForm.amount}
                    onChange={(e) => setTripExpenseForm({ ...tripExpenseForm, amount: e.target.value })}
                    style={{ ...tripInputStyle, width: "100%", boxSizing: "border-box", paddingLeft: 58 }} />
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={tripLabelStyle}>Categoría</label>
                <input required value={tripExpenseForm.category} aria-label="Categoría del gasto"
                  onChange={(e) => setTripExpenseForm({ ...tripExpenseForm, category: e.target.value })}
                  placeholder="Ej. Alimentación, Transporte, Hotel" list="trip-expense-categories"
                  style={{ ...tripInputStyle, width: "100%", boxSizing: "border-box" }} />
                <datalist id="trip-expense-categories">
                  {[...new Set(["Alimentación", "Transporte", "Hotel", "Entretenimiento", "Compras", "Otros", ...(user?.custom_categories || [])])].map(category => <option key={category} value={category} />)}
                </datalist>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={tripLabelStyle}>Medio de pago</label>
                <select aria-label="Medio de pago" value={tripExpenseForm.credit_card_id}
                  onChange={(e) => setTripExpenseForm({ ...tripExpenseForm, credit_card_id: e.target.value, account_id: "unassigned" })}
                  style={{ ...tripInputStyle, width: "100%", background: "#FFFFFF" }}>
                  <option value="">Cuenta, billetera o efectivo</option>
                  {tripCreditCards.map(card => <option key={card.id} value={card.id}>Tarjeta: {card.name}</option>)}
                </select>
                {!tripExpenseForm.credit_card_id && <AccountSelect value={tripExpenseForm.account_id} onChange={account_id => setTripExpenseForm({ ...tripExpenseForm, account_id })} />}
                <p style={{ margin: 0, fontSize: 12, color: "#687377" }}>
                  {tripExpenseForm.credit_card_id ? "Se sumará a la deuda de tu tarjeta sin descontar de caja." : "Se descontará de tu balance y dinero en caja."}
                  {expenseTrip.currency !== user?.currency && " Se aplicará la tasa de cambio disponible al registrar el gasto."}
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={tripLabelStyle}>Describe tu gasto <span style={{ fontWeight: 400 }}>(opcional)</span></label>
                <input type="text" placeholder="Ejem: Cena en restaurante"
                  value={tripExpenseForm.description}
                  onChange={(e) => setTripExpenseForm({ ...tripExpenseForm, description: e.target.value })}
                  style={{ ...tripInputStyle, width: "100%", boxSizing: "border-box" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={tripLabelStyle}>Fecha del gasto</label>
                <input type="date" required max={getLocalDateInputValue()}
                  value={tripExpenseForm.transaction_date}
                  onChange={(e) => setTripExpenseForm({ ...tripExpenseForm, transaction_date: e.target.value })}
                  style={{ ...tripInputStyle, width: "100%", boxSizing: "border-box" }} />
              </div>
              <button type="submit" disabled={isSavingTripExpense} style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", height: 50,
                background: "#396BE9", borderRadius: 16, border: "none", cursor: isSavingTripExpense ? "not-allowed" : "pointer",
                opacity: isSavingTripExpense ? 0.65 : 1, fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 16, color: "#FFFFFF",
              }}>
                {isSavingTripExpense ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Plus size={18} /> Agregar gasto</>}
              </button>
            </form>
          </div>
        </>
      )}

      {/* Create Modal */}
      {isNewTripOpen && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}
            onClick={() => setIsNewTripOpen(false)} />
          <div style={{
            position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "center",
            padding: "24px 28px", gap: 14, width: 480, maxWidth: "calc(100vw - 40px)",
            background: "#FFFFFF", boxShadow: "-2px 0px 20px rgba(176,179,189,0.25)",
            borderRadius: 20,
          }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", width: "100%", position: "relative" }}>
              <h2 style={{
                margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 700,
                fontSize: 20, lineHeight: "28px", color: "#1B2559", textAlign: "center",
              }}>Planear mi próximo viaje</h2>
              <button onClick={() => setIsNewTripOpen(false)} style={{
                position: "absolute", right: -4, top: -4,
                background: "none", border: "none", cursor: "pointer", padding: 4,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#255E99" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Dashed circle icon preview */}
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              border: "1px dashed #4A4A4A", display: "flex",
              alignItems: "center", justifyContent: "center",
              overflow: "hidden", flexShrink: 0,
            }}>
              {formData.icon && formData.icon.startsWith("/") ? (
                <img src={formData.icon} alt="Icono" style={{ width: 56, height: 56, objectFit: "contain" }} />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#396BE9" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              )}
            </div>

            <form onSubmit={handleCreateTrip} style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
              {/* Destino + Presupuesto row */}
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                  <label style={tripLabelStyle}>Destino</label>
                  <input type="text" required autoFocus placeholder="Ejem: Argentina"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    style={tripInputStyle} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                  <label style={tripLabelStyle}>Presupuesto</label>
                  <CurrencyInput
                    currency={formData.currency === "OTRA" ? "COP" : (formData.currency || "COP")}
                    placeholder="0"
                    value={formData.budget}
                    onChange={e => setFormData({...formData, budget: e.target.value})}
                    style={{ ...tripInputStyle, paddingLeft: 34 }}
                  />
                </div>
              </div>

              {/* Fecha del viaje + Moneda row */}
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                  <label style={tripLabelStyle}>Fecha del viaje</label>
                  <Popover open={createDateOpen} onOpenChange={setCreateDateOpen}>
                    <PopoverTrigger asChild>
                      <button type="button" style={{
                        ...tripInputStyle,
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        cursor: "pointer", background: "#FFFFFF", textAlign: "left",
                        color: formData.trip_date ? "#4A4A4A" : "#A89E9E",
                      }}>
                        <span>{formData.trip_date
                          ? new Date(formData.trip_date + "T12:00:00").toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" })
                          : "Seleccionar fecha"}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM19 7H5V6H19V7Z" fill="#396BE9"/>
                        </svg>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-3xl shadow-2xl border-0 overflow-hidden" align="start"
                      style={{ background: "rgba(255,255,255,0.97)", backdropFilter: "blur(24px)", zIndex: 10050 }}>
                      <Calendar
                        mode="single"
                        defaultMonth={formData.trip_date ? new Date(formData.trip_date + "T12:00:00") : new Date()}
                        disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0) + 24 * 60 * 60 * 1000) }}
                        onSelect={(date) => {
                          if (date) {
                            const y = date.getFullYear();
                            const m = String(date.getMonth() + 1).padStart(2, "0");
                            const d = String(date.getDate()).padStart(2, "0");
                            setFormData({ ...formData, trip_date: `${y}-${m}-${d}` });
                          } else {
                            setFormData({ ...formData, trip_date: "" });
                          }
                          setCreateDateOpen(false);
                        }}
                        numberOfMonths={1}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 0 }}>
                  <label htmlFor="trip-currency" style={tripLabelStyle}>Moneda</label>
                  <CurrencySelect id="trip-currency" label="Moneda del viaje"
                    value={formData.currency}
                    onValueChange={currency => setFormData({ ...formData, currency })} />
                </div>
              </div>

              {/* Icon grid */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={tripLabelStyle}>Selecciona un icono</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
                  {TRIP_ICON_OPTIONS.map(icon => (
                    <button key={icon.src} type="button" onClick={() => setFormData({...formData, icon: icon.src})}
                      style={{
                        boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 8, width: "100%", aspectRatio: "1",
                        background: formData.icon === icon.src ? "rgba(225,233,252,1)" : "rgba(225,233,252,0.73)",
                        border: formData.icon === icon.src ? "2px solid #396BE9" : "1px solid rgba(225,233,252,0.73)",
                        borderRadius: 16, cursor: "pointer", transition: "all 0.15s",
                      }}>
                      <img src={icon.src} alt={icon.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Button */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 2 }}>
                <button type="submit" style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: "100%", height: 44, background: "#396BE9", borderRadius: 16,
                  border: "none", cursor: "pointer",
                  fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15,
                  color: "#FFFFFF", transition: "opacity 0.15s",
                }}>
                  Crear viaje
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {isEditTripOpen && tripToEdit && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}
            onClick={() => {setIsEditTripOpen(false); setTripToEdit(null);}} />
          <div style={{
            position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "center",
            padding: "24px 28px", gap: 14, width: 480, maxWidth: "calc(100vw - 40px)",
            background: "#FFFFFF", boxShadow: "-2px 0px 20px rgba(176,179,189,0.25)",
            borderRadius: 20,
          }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", width: "100%", position: "relative" }}>
              <h2 style={{
                margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 700,
                fontSize: 20, lineHeight: "28px", color: "#1B2559", textAlign: "center",
              }}>Configurar viaje</h2>
              <button onClick={() => {setIsEditTripOpen(false); setTripToEdit(null);}} style={{
                position: "absolute", right: -4, top: -4,
                background: "none", border: "none", cursor: "pointer", padding: 4,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#255E99" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Dashed circle icon preview */}
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              border: "1px dashed #4A4A4A", display: "flex",
              alignItems: "center", justifyContent: "center",
              overflow: "hidden", flexShrink: 0,
            }}>
              {editFormData.icon && editFormData.icon.startsWith("/") ? (
                <img src={editFormData.icon} alt="Icono" style={{ width: 56, height: 56, objectFit: "contain" }} />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#396BE9" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              )}
            </div>

            <form onSubmit={handleUpdateTrip} style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
              {/* Destino + Presupuesto row */}
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                  <label style={tripLabelStyle}>Destino</label>
                  <input type="text" required placeholder="Ejem: Argentina"
                    value={editFormData.name}
                    onChange={e => setEditFormData({...editFormData, name: e.target.value})}
                    style={tripInputStyle} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                  <label style={tripLabelStyle}>Presupuesto</label>
                  <CurrencyInput
                    currency={editFormData.currency === "OTRA" ? "COP" : (editFormData.currency || "COP")}
                    placeholder="0"
                    value={editFormData.budget}
                    onChange={e => setEditFormData({...editFormData, budget: e.target.value})}
                    style={{ ...tripInputStyle, paddingLeft: 34 }}
                  />
                </div>
              </div>

              {/* Fecha del viaje + Moneda row */}
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                  <label style={tripLabelStyle}>Fecha del viaje</label>
                  <Popover open={editDateOpen} onOpenChange={setEditDateOpen}>
                    <PopoverTrigger asChild>
                      <button type="button" style={{
                        ...tripInputStyle,
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        cursor: "pointer", background: "#FFFFFF", textAlign: "left",
                        color: editFormData.trip_date ? "#4A4A4A" : "#A89E9E",
                      }}>
                        <span>{editFormData.trip_date
                          ? new Date(editFormData.trip_date + "T12:00:00").toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" })
                          : "Seleccionar fecha"}</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM19 7H5V6H19V7Z" fill="#396BE9"/>
                        </svg>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-3xl shadow-2xl border-0 overflow-hidden" align="start"
                      style={{ background: "rgba(255,255,255,0.97)", backdropFilter: "blur(24px)", zIndex: 10050 }}>
                      <Calendar
                        mode="single"
                        defaultMonth={editFormData.trip_date ? new Date(editFormData.trip_date + "T12:00:00") : new Date()}
                        onSelect={(date) => {
                          if (date) {
                            const y = date.getFullYear();
                            const m = String(date.getMonth() + 1).padStart(2, "0");
                            const d = String(date.getDate()).padStart(2, "0");
                            setEditFormData({...editFormData, trip_date: `${y}-${m}-${d}`});
                          } else {
                            setEditFormData({...editFormData, trip_date: ""});
                          }
                          setEditDateOpen(false);
                        }}
                        numberOfMonths={1}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 0 }}>
                  <label htmlFor="edit-trip-currency" style={tripLabelStyle}>Moneda</label>
                  <CurrencySelect id="edit-trip-currency" label="Moneda del viaje"
                    value={editFormData.currency}
                    onValueChange={currency => setEditFormData({ ...editFormData, currency })} />
                </div>
              </div>

              {/* Icon grid */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={tripLabelStyle}>Selecciona un icono</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
                  {TRIP_ICON_OPTIONS.map(icon => (
                    <button key={icon.src} type="button" onClick={() => setEditFormData({...editFormData, icon: icon.src})}
                      style={{
                        boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 8, width: "100%", aspectRatio: "1",
                        background: editFormData.icon === icon.src ? "rgba(225,233,252,1)" : "rgba(225,233,252,0.73)",
                        border: editFormData.icon === icon.src ? "2px solid #396BE9" : "1px solid rgba(225,233,252,0.73)",
                        borderRadius: 16, cursor: "pointer", transition: "all 0.15s",
                      }}>
                      <img src={icon.src} alt={icon.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 2 }}>
                <button type="submit" style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: "100%", height: 44, background: "#396BE9", borderRadius: 16,
                  border: "none", cursor: "pointer",
                  fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 15,
                  color: "#FFFFFF", transition: "opacity 0.15s",
                }}>
                  Guardar cambios
                </button>

                {tripToEdit.status !== "closed" && (
                  <span
                    onClick={() => handleCloseTrip(tripToEdit.id)}
                    style={{
                      fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 14,
                      color: "#396BE9", cursor: "pointer", textAlign: "center", marginTop: 2,
                    }}
                  >
                    Finalizar Viaje
                  </span>
                )}

                <span
                  onClick={() => handleDeleteTrip(tripToEdit.id)}
                  style={{
                    fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 14,
                    color: "#FF0040", cursor: "pointer", textAlign: "center", marginTop: 2,
                  }}
                >
                  Eliminar Viaje
                </span>
              </div>
            </form>
          </div>
        </>
      )}

      {/* History Modal has been moved to TripHistoryPage.jsx */}

      <style>{`
        /* ── Mobile: trips cards 1 column ── */
        @media (max-width: 900px) {
          .aorra-trips-cards-grid {
            grid-template-columns: 1fr !important;
          }
          .aorra-history-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          .aorra-history-modal-container {
            width: 100% !important;
            max-width: calc(100vw - 20px) !important;
            padding: 20px !important;
          }
        }
        /* ── Desktop: restore 3-column KPI layout ── */
        @media (min-width: 901px) {
          .aorra-trips-kpi-outer {
            display: grid !important;
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            flex-direction: unset !important;
          }
          .aorra-trips-kpi-row2 {
            display: contents !important;
          }
          .aorra-trips-kpi-row2 > div {
            padding: 9px 30px !important;
            gap: 30px !important;
          }
          .aorra-trips-kpi-row2 > div > div:first-child {
            width: 80px !important;
            height: 80px !important;
          }
          .aorra-trips-kpi-row2 > div > div:first-child img {
            width: 49.23px !important;
            height: 49.23px !important;
          }
          .aorra-trips-kpi-row2 > div > div:last-child > span:first-child {
            font-size: 18px !important;
          }
          .aorra-trips-kpi-row2 > div > div:last-child > span:last-child {
            font-size: 24px !important;
          }
          .aorra-trips-cards-grid {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          }
        }
      `}</style>
    </div>
  );
}

/* ─── Trip Icon Options ─── */
const TRIP_ICON_OPTIONS = [
  { src: "/Iconos/Viajes/avion.png", label: "Avión" },
  { src: "/Iconos/Viajes/camara.png", label: "Cámara" },
  { src: "/Iconos/Viajes/playa.png", label: "Playa" },
  { src: "/Iconos/Viajes/ciudad.png", label: "Ciudad" },
  { src: "/Iconos/Viajes/maletas.png", label: "Maletas" },
  { src: "/Iconos/Viajes/campamento.png", label: "Campamento" },
  { src: "/Iconos/Viajes/mapa.png", label: "Mapa" },
  { src: "/Iconos/Viajes/hotel.png", label: "Hotel" },
  { src: "/Iconos/Viajes/pasaporte.png", label: "Pasaporte" },
  { src: "/Iconos/Viajes/barco.png", label: "Barco" },
  { src: "/Iconos/Viajes/brujula.png", label: "Brújula" },
  { src: "/Iconos/Viajes/nevado.png", label: "Nevado" },
];

/* ─── Shared styles ─── */
const tripLabelStyle = {
  fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 14,
  lineHeight: "20px", color: "#5C5C5C",
};

const tripInputStyle = {
  boxSizing: "border-box", width: "100%", height: 40, padding: "8px 12px",
  border: "1px solid #DDDDDD", borderRadius: 20, outline: "none",
  fontFamily: "'Manrope', sans-serif", fontWeight: 400, fontSize: 14, color: "#4A4A4A",
};
